import React, { useMemo } from 'react';
import { ArrowRight, Wand2, Play, Type } from 'lucide-react';
import type { WorkflowNode, WorkflowConnection, WorkflowResponse } from '@/types/agent';
import { NODE_TYPE_CONFIG } from '@/config/nodeTypeConfig';
import type { NodeType } from '@/types';

const DEFAULT_CONFIG = { icon: Type, color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20', label: '节点' };

interface WorkflowNodeCardProps {
  node: WorkflowNode;
}

const WorkflowNodeCard: React.FC<WorkflowNodeCardProps> = ({ node }) => {
  const config = NODE_TYPE_CONFIG[node.type as NodeType] || DEFAULT_CONFIG;
  const Icon = config.icon;
  const prompt = node.data?.prompt;

  return (
    <div className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border ${config.bg || ''} ${config.border} min-w-[140px] max-w-[200px]`}>
      <Icon size={14} className={config.color} />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium text-gray-200 truncate">{node.title}</div>
        {prompt && (
          <div className="text-[10px] text-gray-500 truncate mt-0.5">{prompt}</div>
        )}
      </div>
    </div>
  );
};

interface WorkflowPreviewProps {
  workflow: WorkflowResponse;
  onApply?: (workflow: WorkflowResponse) => void;
}

export const WorkflowPreview: React.FC<WorkflowPreviewProps> = ({ workflow, onApply }) => {
  const nodeMap = useMemo(() => {
    const map = new Map<string, WorkflowNode>();
    workflow.nodes.forEach(n => map.set(n.id, n));
    return map;
  }, [workflow.nodes]);

  const sortedNodes = useMemo(() => {
    const inDegree = new Map<string, number>();
    workflow.nodes.forEach(n => inDegree.set(n.id, 0));
    workflow.connections.forEach(c => {
      inDegree.set(c.to, (inDegree.get(c.to) || 0) + 1);
    });

    const visited = new Set<string>();
    const result: WorkflowNode[] = [];
    const queue: string[] = [];

    inDegree.forEach((deg, id) => {
      if (deg === 0) queue.push(id);
    });

    while (queue.length > 0) {
      const id = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      const node = nodeMap.get(id);
      if (node) result.push(node);

      workflow.connections
        .filter(c => c.from === id)
        .forEach(c => {
          const newDeg = (inDegree.get(c.to) || 1) - 1;
          inDegree.set(c.to, newDeg);
          if (newDeg === 0) queue.push(c.to);
        });
    }

    workflow.nodes.forEach(n => {
      if (!visited.has(n.id)) result.push(n);
    });

    return result;
  }, [workflow.nodes, workflow.connections, nodeMap]);

  const connectionPairs = useMemo(() => {
    return workflow.connections
      .map(c => ({
        from: sortedNodes.findIndex(n => n.id === c.from),
        to: sortedNodes.findIndex(n => n.id === c.to),
      }))
      .filter(p => p.from >= 0 && p.to >= 0);
  }, [workflow.connections, sortedNodes]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] font-medium">
          🎨 工作流
        </span>
        <span className="text-gray-600">{workflow.nodes.length} 个节点</span>
        <span className="text-gray-600">·</span>
        <span className="text-gray-600">{workflow.connections.length} 个连接</span>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-thin">
        {sortedNodes.map((node, idx) => (
          <React.Fragment key={node.id}>
            <WorkflowNodeCard node={node} />
            {connectionPairs.some(p => p.from === idx) && (
              <ArrowRight size={12} className="text-gray-600 shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>

      {onApply && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onApply(workflow)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[11px] font-medium hover:bg-blue-500/20 transition-colors"
          >
            <Wand2 size={12} />
            应用到画布
          </button>
          {workflow.autoExecute && (
            <button
              onClick={() => onApply({ ...workflow, autoExecute: true })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-[11px] font-medium hover:bg-green-500/20 transition-colors"
            >
              <Play size={12} />
              应用并执行
            </button>
          )}
        </div>
      )}
    </div>
  );
};
